/**
 * ConnectionsPage - Multi-Mode Connection Management
 * 
 * A calm, intentional space for managing connections across modes:
 * - Dating
 * - Friendship
 * - Business
 * - Community
 * 
 * Features:
 * - Set connection mode & visibility preferences
 * - View incoming/outgoing requests
 * - Accept/decline requests
 * - View accepted connections
 * - Block/unblock users
 */
import React, { useCallback, useEffect, useMemo, useState, useContext } from "react";
import apiClient from "../api/client";
import { ConnectionsAPI } from "../api/connectionsApi";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import { openChatAndNavigate } from "../services/chat";
import { ProfileViewModal } from "../components/profile/ProfileViewModal";
import { useProfileViewer } from "../hooks/useProfileViewer";
import InlineNotice from "../components/common/InlineNotice";
import { useInlineNotice } from "../hooks/useInlineNotice";
import { PageShell } from "../components/layout/PageShell";

const MODES = [
  { key: "dating", label: "💕 Dating", emoji: "💕" },
  { key: "friendship", label: "🤝 Friendship", emoji: "🤝" },
  { key: "business", label: "💼 Business", emoji: "💼" },
  { key: "community", label: "🌍 Community", emoji: "🌍" },
];

const VIS = [
  { key: "public", label: "🌐 Public" },
  { key: "connections_only", label: "🔒 Connections only" },
];

function Pill({ children, variant = "default" }) {
  const variants = {
    default: "bg-slate-100 text-slate-700 border-slate-200",
    success: "bg-emerald-100 text-emerald-700 border-emerald-200",
    warning: "bg-amber-100 text-amber-700 border-amber-200",
    danger: "bg-rose-100 text-rose-700 border-rose-200",
  };
  
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${variants[variant]}`}>
      {children}
    </span>
  );
}

/** Tab Button for Inbox navigation */
function TabButton({ active, onClick, children, count }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium transition-colors ${
        active
          ? "border-rose-300 bg-rose-50 text-rose-700 ring-2 ring-rose-200"
          : "border-slate-200 text-slate-700 hover:bg-slate-50"
      }`}
      aria-current={active ? "page" : undefined}
    >
      <span>{children}</span>
      {typeof count === "number" && (
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
          active ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"
        }`}>
          {count}
        </span>
      )}
    </button>
  );
}

/** Empty state component for inbox tabs */
function InboxEmptyState({ title, body, emoji = "🌙" }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/50 p-8 text-center">
      <div className="text-3xl mb-3">{emoji}</div>
      <h3 className="text-base font-semibold text-slate-700 mb-2">{title}</h3>
      <p className="text-sm text-slate-500 max-w-sm mx-auto">{body}</p>
    </div>
  );
}

/** Intent pill for connection cards */
function IntentPill({ intent }) {
  const emojiMap = {
    dating: "💕",
    friendship: "🤝",
    business: "💼",
    community: "🌍",
  };
  const label = intent ? intent.charAt(0).toUpperCase() + intent.slice(1) : "Connection";
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-600 pointer-events-none select-none">
      <span>{emojiMap[intent] || "🤝"}</span>
      <span>{label}</span>
    </span>
  );
}

/** Connection inbox row card - mobile-optimized layout */
function InboxRowCard({ user, intent, meta, right, onClick }) {
  const name = user?.display_name || user?.name || "Unknown";
  const avatarUrl = user?.avatar_url || user?.photo_url;
  
  return (
    <div 
      className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 hover:border-slate-300 transition-colors"
      role={onClick ? "button" : undefined}
      onClick={onClick}
      tabIndex={onClick ? 0 : undefined}
    >
      {/* Avatar - fixed size */}
      <div className="h-12 w-12 overflow-hidden rounded-full border-2 border-slate-200 bg-gradient-to-br from-rose-100 to-purple-100 flex items-center justify-center shrink-0">
        {avatarUrl ? (
          <img src={avatarUrl} alt={name} className="h-full w-full object-cover" />
        ) : (
          <span className="text-lg opacity-60">💜</span>
        )}
      </div>
      
      {/* Content area - flexible */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          {/* Name + metadata */}
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-slate-900">{name}</div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <IntentPill intent={intent} />
              {meta && <span className="text-xs text-slate-500 pointer-events-none">{meta}</span>}
            </div>
          </div>
          
          {/* Action buttons - always visible, never wrapped under metadata */}
          <div className="flex shrink-0 items-center gap-2">{right}</div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children, right, count }) {
  return (
    <div className="rounded-2xl border border-slate-200 p-5 shadow-sm bg-white">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
          {typeof count === 'number' && (
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 text-xs font-medium text-slate-600">
              {count}
            </span>
          )}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function UserRow({ edge, actions }) {
  const u = edge?.other_user || {};
  const name = u.display_name || u.name || "User";
  const mode = u.connection_mode || "dating";
  const modeEmoji = MODES.find(m => m.key === mode)?.emoji || "💕";

  return (
    <div className="flex items-center justify-between gap-3 py-3">
      <div className="flex items-center gap-3 min-w-0">
        <div className="h-12 w-12 rounded-full border-2 border-slate-200 overflow-hidden bg-gradient-to-br from-rose-100 to-purple-100 flex items-center justify-center text-lg shrink-0">
          {u.avatar_url ? (
            <img alt={name} src={u.avatar_url} className="h-full w-full object-cover" />
          ) : (
            <span className="opacity-60">💜</span>
          )}
        </div>

        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            <span className="font-medium text-slate-800 truncate">{name}</span>
            <Pill>{modeEmoji} {mode}</Pill>
            {u.verified_status && <Pill variant="success">✓ verified</Pill>}
          </div>
          <div className="text-xs text-slate-400 truncate mt-0.5">
            {edge.intent && <span className="mr-2">Intent: {edge.intent}</span>}
            {edge.created_at && <span>• {new Date(edge.created_at).toLocaleDateString()}</span>}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">{actions}</div>
    </div>
  );
}

function ActionButton({ onClick, disabled, variant = "default", children, "aria-label": ariaLabel }) {
  const variants = {
    default: "border-slate-200 hover:bg-slate-50 text-slate-700",
    primary: "border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-700",
    danger: "border-rose-200 hover:bg-rose-50 text-rose-600",
    success: "border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700",
  };
  
  return (
    <button
      className={`rounded-xl border px-3 py-2 text-sm font-medium transition-colors active:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed ${variants[variant]}`}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  );
}

/** Connection Confidence Toast */
function ConnectionToast({ toast, onDismiss }) {
  if (!toast) return null;

  return (
    <div className="fixed bottom-4 left-1/2 z-[9999] w-[92%] max-w-md -translate-x-1/2 rounded-2xl border border-slate-200 bg-white p-4 shadow-lg animate-in slide-in-from-bottom-4">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div className="text-sm font-semibold text-slate-900">{toast.title}</div>
          {toast.body && <div className="mt-1 text-sm text-slate-600">{toast.body}</div>}
        </div>
        <button
          className="rounded-lg border border-slate-200 px-2 py-1 text-sm text-slate-500 hover:bg-slate-50"
          onClick={onDismiss}
          aria-label="Dismiss notification"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

export default function ConnectionsPage() {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  
  // Canonical profile viewer hook - same as Dashboard, Matches, etc.
  const profileViewer = useProfileViewer();
  
  const [prefs, setPrefs] = useState(null);
  const [lists, setLists] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  
  // Use InlineNotice hook for consistent error handling
  const { notice, showError, showSuccess, showInfo, dismissNotice } = useInlineNotice("connections", token);
  
  // Legacy state for backward compatibility (will be migrated)
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");

  // Inbox tab state: incoming | sent | connected
  const [inboxTab, setInboxTab] = useState("incoming");
  
  // Per-action busy state for inbox items (prevents double-click)
  const [actionBusy, setActionBusy] = useState({}); // { [edgeId]: true }

  // P4: Per-user message busy state
  const [msgBusyByUser, setMsgBusyByUser] = useState({});
  const setMsgBusy = useCallback((userId, v) => 
    setMsgBusyByUser((p) => ({ ...p, [String(userId)]: v })), []);

  // P3: Track seen request IDs for "NEW" pill (localStorage)
  const [seenRequestIds, setSeenRequestIds] = useState(() => {
    try {
      const stored = localStorage.getItem('connections_seen_requests');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Connection Awareness ("Who's Here")
  const [counts, setCounts] = useState(null);
  const [suggestMode, setSuggestMode] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showMoreCooldown, setShowMoreCooldown] = useState(false);
  const [showHiddenDrawer, setShowHiddenDrawer] = useState(false);
  const [hiddenUsers, setHiddenUsers] = useState([]);

  // Trust Invites
  const [invites, setInvites] = useState([]);
  const [invitesBusy, setInvitesBusy] = useState(false);
  const [inviteMode, setInviteMode] = useState("friendship");
  const [inviteTtlHours, setInviteTtlHours] = useState(168); // 7 days default
  const [inviteMaxUses, setInviteMaxUses] = useState(1);
  const [inviteNote, setInviteNote] = useState("");
  const [lastInviteLink, setLastInviteLink] = useState("");

  // Test send request
  const [sendUserId, setSendUserId] = useState("");
  const [sendIntent, setSendIntent] = useState("friendship");

  // Connection Confidence Loop - per-user status tracking
  // statuses: idle | sending | pending | error
  const [connectionStatus, setConnectionStatus] = useState({}); // { `${userId}:${intent}`: status }
  const [toast, setToast] = useState(null);
  
  // Profile View Modal - loading/error state only (modal itself managed by profileViewer hook)
  const [profileModalLoading, setProfileModalLoading] = useState(false);
  const [profileModalError, setProfileModalError] = useState(null);
  
  /**
   * Open profile in modal (for Connected users)
   * Uses canonical profileViewer hook for consistent rendering across app
   */
  const openProfileModal = useCallback(async (userId, displayName) => {
    if (!userId) {
      setProfileModalError("Invalid profile ID");
      return;
    }
    if (!token) {
      setProfileModalError("Please log in to view profiles");
      return;
    }
    
    setProfileModalLoading(true);
    setProfileModalError(null);
    
    try {
      const res = await apiClient.get(`/users/profile/${userId}`);
      
      // Use canonical profile viewer - same as Dashboard, Edit Profile, etc.
      profileViewer.showProfile(res.data, {
        isSelf: false,
        source: "connections:view",
      });
    } catch (e) {
      console.error("[Connections] Profile fetch error:", e);
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "";
      
      if (status === 404) {
        if (detail.includes("not yet created")) {
          setProfileModalError(`${displayName || 'This person'} hasn't set up their profile yet.`);
        } else if (detail.includes("User not found")) {
          setProfileModalError(`This account no longer exists.`);
        } else {
          setProfileModalError(`Profile not found. ${displayName || 'This user'} may have deleted their account.`);
        }
      } else if (status === 403) {
        setProfileModalError("You don't have permission to view this profile.");
      } else {
        setProfileModalError("Couldn't load profile. Please try again.");
      }
    } finally {
      setProfileModalLoading(false);
    }
  }, [token, profileViewer]);

  const getConnectionStatus = useCallback((userId, intent) => {
    return connectionStatus[`${userId}:${intent}`] || "idle";
  }, [connectionStatus]);

  const setUserConnectionStatus = useCallback((userId, intent, status) => {
    setConnectionStatus(prev => ({ ...prev, [`${userId}:${intent}`]: status }));
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);
  
  // Helper to show toast with auto-dismiss
  const showToast = useCallback((title, body, duration = 5000) => {
    setToast({ title, body });
    setTimeout(() => setToast(null), duration);
  }, []);

  const clearMessages = () => {
    setErr("");
    setSuccess("");
  };

  // Legacy showSuccess - now handled by useInlineNotice hook
  const showLegacySuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(""), 3000);
  };

  // P3: Mark incoming requests as seen (for "NEW" pill)
  const markRequestsAsSeen = useCallback((requestIds) => {
    setSeenRequestIds(prev => {
      const newSeen = [...new Set([...prev, ...requestIds])];
      try {
        localStorage.setItem('connections_seen_requests', JSON.stringify(newSeen));
      } catch (e) {
        console.warn('[Connections] Failed to persist seen requests:', e);
      }
      return newSeen;
    });
  }, []);

  // P3: Check if a request is new (unseen)
  const isNewRequest = useCallback((requestId) => {
    return !seenRequestIds.includes(requestId);
  }, [seenRequestIds]);

  // P3: Mark all visible incoming as seen when tab is viewed
  // 6s delay gives users time to notice NEW pills (2s was too fast)
  useEffect(() => {
    if (inboxTab === "incoming" && lists?.incoming_requests?.length > 0) {
      const incomingIds = lists.incoming_requests.map(r => r.id);
      const timer = setTimeout(() => {
        markRequestsAsSeen(incomingIds);
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [inboxTab, lists?.incoming_requests, markRequestsAsSeen]);

  const refresh = async () => {
    clearMessages();
    dismissNotice(); // Clear any previous notice
    setLoading(true);
    try {
      const [p, l, c, inv] = await Promise.all([
        ConnectionsAPI.getPrefs(),
        ConnectionsAPI.listMine(),
        ConnectionsAPI.awarenessCounts(),
        ConnectionsAPI.listMyInvites(10),
      ]);
      setPrefs(p);
      setLists(l);
      setCounts(c?.counts || null);
      setInvites(inv?.invites || []);
      
      // P3: Update global nav incoming count
      if (window.updateIncomingCount) {
        const pendingIncoming = (l?.incoming_requests || []).filter(r => !r.status || r.status === "pending").length;
        window.updateIncomingCount(pendingIncoming);
      }
      
      // Hydrate pending status from outgoing_requests
      // This ensures pending state persists across refresh/navigation
      if (l?.outgoing_requests?.length > 0) {
        const pendingStatuses = {};
        l.outgoing_requests.forEach((edge) => {
          const userId = edge.other_user?.id;
          const intent = edge.intent || "friendship";
          if (userId) {
            pendingStatuses[`${userId}:${intent}`] = "pending";
          }
        });
        setConnectionStatus((prev) => ({ ...prev, ...pendingStatuses }));
      }
      
      // Auto-load suggestions based on user's selected mode (if discoverable)
      if (p?.connection_mode && c?.counts?.[p.connection_mode] > 0) {
        // Auto-load initial batch for their mode
        const res = await ConnectionsAPI.awarenessSuggestions(p.connection_mode, 5);
        setSuggestMode(p.connection_mode);
        setSuggestions(res?.suggestions || []);
      }
    } catch (e) {
      showError(e, { 
        customMessage: "Couldn't load connections. Check your connection and try again.",
        onRetry: refresh,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const setMode = async (modeKey) => {
    if (!prefs) return;
    clearMessages();
    setBusy(true);
    try {
      const next = await ConnectionsAPI.setPrefs({
        connection_mode: modeKey,
        visibility_scope: prefs.visibility_scope || "public",
        discoverable: prefs.discoverable || false,
        discoverable_modes: prefs.discoverable_modes || [],
      });
      setPrefs((prev) => ({ ...(prev || {}), ...next }));
      showSuccess(`Mode changed to ${modeKey}`);
    } catch (e) {
      setErr(e?.message || "Failed to update mode.");
    } finally {
      setBusy(false);
    }
  };

  const setVis = async (visKey) => {
    if (!prefs) return;
    clearMessages();
    setBusy(true);
    try {
      const next = await ConnectionsAPI.setPrefs({
        connection_mode: prefs.connection_mode || "dating",
        visibility_scope: visKey,
      });
      setPrefs((prev) => ({ ...(prev || {}), ...next }));
      showSuccess(`Visibility changed to ${visKey}`);
    } catch (e) {
      setErr(e?.message || "Failed to update visibility.");
    } finally {
      setBusy(false);
    }
  };

  // ✅ PATCH: Safety wrapper to ensure actions only apply to connection edges
  // NOTE: This callback was moved to after incoming/outgoing/accepted are defined
  // to avoid "Cannot access before initialization" error
  const isValidConnectionEdge = useCallback((edgeId) => {
    // Use lists directly since incoming/outgoing/accepted may not be defined yet
    const allEdges = [
      ...(lists?.incoming_requests || []),
      ...(lists?.outgoing_requests || []),
      ...(lists?.connections || [])
    ];
    const isNonEmptyStr = (v) => typeof v === "string" && v.trim().length > 0;
    
    return allEdges.some((e) => {
      const idOk = e?.id === edgeId || e?.edge_id === edgeId;
      const hasOtherUser = !!e?.other_user?.id;
      const looksLikeConnection = isNonEmptyStr(e?.intent) && isNonEmptyStr(e?.status);
      const sourceOk = e?.source === "connection" || (!e?.source && looksLikeConnection);
      return idOk && hasOtherUser && sourceOk;
    });
  }, [lists]);

  const accept = async (edgeId) => {
    // Per-action busy to prevent double-click
    if (actionBusy[edgeId]) return;
    
    // ✅ Safety: Only accept connection edges (not flirts)
    if (!isValidConnectionEdge(edgeId)) {
      console.warn('[Connections] Attempted to accept non-connection edge:', edgeId);
      return;
    }
    
    setActionBusy(prev => ({ ...prev, [edgeId]: true }));
    clearMessages();
    dismissNotice();
    
    try {
      await ConnectionsAPI.respondRequest(edgeId, { accept: true });
      // P3: Post-accept nudge with next action hint
      showSuccess("Say hey 👋 — send them a message!", { title: "Connected!" });
      await refresh();
    } catch (e) {
      showError(e, { 
        customMessage: "Couldn't accept this request. Please try again.",
        onRetry: () => accept(edgeId),
      });
    } finally {
      setActionBusy(prev => {
        const next = { ...prev };
        delete next[edgeId];
        return next;
      });
    }
  };

  const decline = async (edgeId) => {
    // Per-action busy to prevent double-click
    if (actionBusy[edgeId]) return;
    
    // ✅ Safety: Only decline connection edges (not flirts)
    if (!isValidConnectionEdge(edgeId)) {
      console.warn('[Connections] Attempted to decline non-connection edge:', edgeId);
      return;
    }
    
    setActionBusy(prev => ({ ...prev, [edgeId]: true }));
    clearMessages();
    dismissNotice();
    
    try {
      await ConnectionsAPI.respondRequest(edgeId, { accept: false });
      showInfo("No worries — you're in control.", { title: "Request declined" });
      await refresh();
    } catch (e) {
      showError(e, { 
        customMessage: "Couldn't decline this request. Please try again.",
        onRetry: () => decline(edgeId),
      });
    } finally {
      setActionBusy(prev => {
        const next = { ...prev };
        delete next[edgeId];
        return next;
      });
    }
  };

  const block = async (otherUserId) => {
    clearMessages();
    dismissNotice();
    setBusy(true);
    try {
      await ConnectionsAPI.blockUser({ other_user_id: otherUserId });
      showSuccess("They won't be able to contact you again.", { title: "User blocked" });
      await refresh();
    } catch (e) {
      showError(e, { 
        customMessage: "Couldn't block this user. Please try again.",
      });
    } finally {
      setBusy(false);
    }
  };

  const unblock = async (otherUserId) => {
    clearMessages();
    dismissNotice();
    setBusy(true);
    try {
      await ConnectionsAPI.unblockUser({ other_user_id: otherUserId });
      showSuccess("They may appear in your suggestions again.", { title: "User unblocked" });
      await refresh();
    } catch (e) {
      showError(e, { 
        customMessage: "Couldn't unblock this user. Please try again.",
      });
    } finally {
      setBusy(false);
    }
  };

  const sendRequest = async () => {
    const other = sendUserId.trim();
    if (!other) return;
    clearMessages();
    setBusy(true);
    try {
      const result = await ConnectionsAPI.sendRequest({ other_user_id: other, intent: sendIntent });
      setSendUserId("");
      showSuccess(result.message || "Interest sent!");
      await refresh();
    } catch (e) {
      setErr(e?.message || "Failed to send request.");
    } finally {
      setBusy(false);
    }
  };

  // ==========================================================================
  // CONNECTION AWARENESS FUNCTIONS
  // ==========================================================================

  const loadSuggestions = async (mode) => {
    clearMessages();
    setBusy(true);
    try {
      const res = await ConnectionsAPI.awarenessSuggestions(mode, 5);
      setSuggestMode(mode);
      setSuggestions(res?.suggestions || []);
    } catch (e) {
      setErr(e?.message || "Failed to load suggestions.");
    } finally {
      setBusy(false);
    }
  };

  const showMore = async () => {
    if (!suggestMode || showMoreCooldown) return;
    
    // Rate limit: 3 second cooldown
    setShowMoreCooldown(true);
    setTimeout(() => setShowMoreCooldown(false), 3000);
    
    await loadSuggestions(suggestMode);
  };

  const hideSuggestion = async (id) => {
    clearMessages();
    setBusy(true);
    try {
      await ConnectionsAPI.awarenessHide(id);
      const hiddenUser = suggestions.find((x) => x.id === id);
      setSuggestions((prev) => prev.filter((x) => x.id !== id));
      if (hiddenUser) {
        setHiddenUsers((prev) => [...prev, { ...hiddenUser, hidden_at: new Date().toISOString() }]);
      }
      showSuccess("User hidden from suggestions.");
    } catch (e) {
      setErr(e?.message || "Failed to hide.");
    } finally {
      setBusy(false);
    }
  };

  const unhideSuggestion = async (id) => {
    clearMessages();
    setBusy(true);
    try {
      await ConnectionsAPI.awarenessUnhide(id);
      setHiddenUsers((prev) => prev.filter((x) => x.id !== id));
      showSuccess("User unhidden. They may appear in future suggestions.");
    } catch (e) {
      setErr(e?.message || "Failed to unhide.");
    } finally {
      setBusy(false);
    }
  };

  const sendFromSuggestion = async (id, intent) => {
    clearMessages();
    
    // Double-send prevention - check per-user status
    const currentStatus = getConnectionStatus(id, intent);
    if (currentStatus === "sending" || currentStatus === "pending") {
      return; // Already in progress or sent
    }

    setUserConnectionStatus(id, intent, "sending");

    try {
      await ConnectionsAPI.sendRequest({ other_user_id: id, intent });
      
      // Mark as pending (shows badge)
      setUserConnectionStatus(id, intent, "pending");
      
      // Show toast with "what happens next" microcopy
      setToast({
        title: "Connection request sent ✅",
        body: "They'll see it in their Connections. You'll be notified when they respond.",
      });

      // Auto-dismiss toast after 6 seconds
      setTimeout(() => setToast(null), 6000);

      // Remove from suggestions list
      setSuggestions((prev) => prev.filter((x) => x.id !== id));
      
      await refresh();
    } catch (e) {
      setUserConnectionStatus(id, intent, "error");
      setErr(e?.message || "Failed to send request.");
      setToast({
        title: "Couldn't send request",
        body: "Check your connection and try again.",
      });
    }
  };

  const toggleDiscoverable = async () => {
    if (!prefs) return;
    clearMessages();
    setBusy(true);
    try {
      const next = await ConnectionsAPI.setPrefs({
        connection_mode: prefs.connection_mode || "dating",
        visibility_scope: prefs.visibility_scope || "public",
        discoverable: !prefs.discoverable,
        discoverable_modes: prefs.discoverable_modes || [],
      });
      setPrefs((prev) => ({ ...(prev || {}), ...next }));
      showSuccess(next.discoverable ? "You're now discoverable!" : "Discovery turned off.");
    } catch (e) {
      setErr(e?.message || "Failed to update discoverability.");
    } finally {
      setBusy(false);
    }
  };

  // ==========================================================================
  // TRUST INVITES FUNCTIONS
  // ==========================================================================

  const buildInviteLink = (code) => `${window.location.origin}/invite/${code}`;

  const createInvite = async () => {
    clearMessages();
    setInvitesBusy(true);
    setLastInviteLink("");
    try {
      const maxUsesVal =
        inviteMaxUses === "" || inviteMaxUses === null ? null : Number(inviteMaxUses);
      const ttlVal =
        inviteTtlHours === "" || inviteTtlHours === null ? null : Number(inviteTtlHours);

      const res = await ConnectionsAPI.createInvite({
        mode: inviteMode,
        ttl_hours: ttlVal,
        max_uses: maxUsesVal,
        note: inviteNote?.trim() || "",
      });

      const link = buildInviteLink(res.code);
      setLastInviteLink(link);

      // Refresh recent invites list
      const inv = await ConnectionsAPI.listMyInvites(10);
      setInvites(inv?.invites || []);

      // Copy immediately (nice UX)
      try {
        await navigator.clipboard.writeText(link);
        showSuccess("Invite created and copied to clipboard!");
      } catch (_) {
        showSuccess("Invite created!");
      }
    } catch (e) {
      setErr(e?.message || "Failed to create invite.");
    } finally {
      setInvitesBusy(false);
    }
  };

  const copyText = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      showSuccess("Copied to clipboard!");
    } catch (_) {
      // ignore
    }
  };

  const refreshInvites = async () => {
    setInvitesBusy(true);
    try {
      const inv = await ConnectionsAPI.listMyInvites(10);
      setInvites(inv?.invites || []);
    } catch (e) {
      setErr(e?.message || "Failed to load invites.");
    } finally {
      setInvitesBusy(false);
    }
  };

  // Memoize the list arrays to prevent unnecessary re-renders
  // ✅ PATCH: Hard filter to ensure Connections UI NEVER shows flirts/likes
  // Bulletproof: Non-empty strings + edge ID required
  const isNonEmptyString = (v) => typeof v === "string" && v.trim().length > 0;
  
  const onlyConnectionEdges = useCallback((items) => 
    (items || []).filter((x) => {
      const hasOtherUser = !!x?.other_user?.id;
      const hasEdgeId = !!(x?.edge_id || x?.id);
      const looksLikeConnection = isNonEmptyString(x?.status) && isNonEmptyString(x?.intent);
      const sourceOk = x?.source === "connection" || (!x?.source && looksLikeConnection);
      return sourceOk && hasOtherUser && hasEdgeId;
    }), []);

  const incoming = useMemo(() => onlyConnectionEdges(lists?.incoming_requests), [lists?.incoming_requests, onlyConnectionEdges]);
  const outgoing = useMemo(() => onlyConnectionEdges(lists?.outgoing_requests), [lists?.outgoing_requests, onlyConnectionEdges]);
  const accepted = useMemo(() => onlyConnectionEdges(lists?.connections), [lists?.connections, onlyConnectionEdges]);
  const blocked = useMemo(() => lists?.blocked || [], [lists?.blocked]);

  // Inbox tab counts - filter to pending only for accuracy
  const inboxCounts = useMemo(() => ({
    incoming: incoming.filter(r => !r.status || r.status === "pending").length,
    sent: outgoing.filter(r => !r.status || r.status === "pending").length,
    connected: accepted.length,
  }), [incoming, outgoing, accepted.length]);

  // Collapsed state for blocked users section
  const [showBlocked, setShowBlocked] = useState(false);

  const emptyState = useMemo(() => {
    if (!lists) return false;
    return incoming.length === 0 && outgoing.length === 0 && accepted.length === 0 && blocked.length === 0;
  }, [lists, incoming.length, outgoing.length, accepted.length, blocked.length]);

  return (
    <PageShell>
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
                🤝 Connections
              </h1>
              <p className="text-slate-500 mt-2 max-w-xl">
                A calm space to build intentional connections — dating, friendship, business, or community. 
                Your existing matches and discovery stay untouched.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Link 
                to="/discover"
                className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              >
                ← Back to Discover
              </Link>
              <ActionButton onClick={refresh} disabled={loading || busy}>
                ↻ Refresh
              </ActionButton>
            </div>
          </div>
        </div>

        {/* Alerts - InlineNotice for consistent error/success handling */}
        {notice && (
          <div className="mb-6" data-testid="connections-notice-container">
            <InlineNotice
              variant={notice.variant}
              title={notice.title}
              actionLabel={notice.actionLabel}
              onAction={notice.onAction}
              href={notice.href}
              testId="connections-notice"
            >
              {notice.message}
            </InlineNotice>
          </div>
        )}
        {/* Legacy alerts (fallback) */}
        {err && !notice && (
          <div className="mb-6 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 flex items-center gap-2">
            <span>⚠️</span> {err}
          </div>
        )}
        {success && !notice && (
          <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700 flex items-center gap-2">
            <span>✓</span> {success}
          </div>
        )}

        {/* Preferences */}
        <Section title="Your Connection Settings">
          {loading && !prefs ? (
            <div className="text-sm text-slate-400 animate-pulse">Loading preferences…</div>
          ) : (
            <>
              <div className="mb-4 p-3 rounded-xl bg-slate-50 border border-slate-100">
                <div className="text-sm text-slate-600">
                  Current mode: <span className="font-semibold text-slate-800">{MODES.find(m => m.key === prefs?.connection_mode)?.label || "Dating"}</span>
                  <span className="mx-2">•</span>
                  Visibility: <span className="font-semibold text-slate-800">{VIS.find(v => v.key === prefs?.visibility_scope)?.label || "Public"}</span>
                  {prefs?.verified_status && (
                    <>
                      <span className="mx-2">•</span>
                      <Pill variant="success">✓ Verified</Pill>
                    </>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Connection Mode</label>
                  <div className="flex flex-wrap items-center gap-2">
                    {MODES.map((m) => (
                      <button
                        key={m.key}
                        className={`rounded-xl border px-4 py-2.5 text-sm font-medium transition-all ${
                          prefs?.connection_mode === m.key 
                            ? "bg-rose-50 border-rose-300 text-rose-700 ring-2 ring-rose-200" 
                            : "border-slate-200 text-slate-600 hover:bg-slate-50"
                        } disabled:opacity-50`}
                        onClick={() => setMode(m.key)}
                        disabled={busy}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Visibility</label>
                  <div className="flex flex-wrap items-center gap-2">
                    {VIS.map((v) => (
                      <button
                        key={v.key}
                        className={`rounded-xl border px-4 py-2.5 text-sm font-medium transition-all ${
                          prefs?.visibility_scope === v.key 
                            ? "bg-slate-100 border-slate-300 text-slate-700 ring-2 ring-slate-200" 
                            : "border-slate-200 text-slate-600 hover:bg-slate-50"
                        } disabled:opacity-50`}
                        onClick={() => setVis(v.key)}
                        disabled={busy}
                      >
                        {v.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Test Utility - Dev only */}
              {process.env.NODE_ENV !== 'production' && (
              <div className="mt-6 rounded-xl border border-dashed border-slate-300 p-4 bg-slate-50/50">
                <div className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <span>🧪</span> Quick Test: Send Request by User ID
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    value={sendUserId}
                    onChange={(e) => setSendUserId(e.target.value)}
                    placeholder="Paste other user's ID (UUID)"
                    className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-200 focus:border-rose-300"
                  />
                  <select
                    value={sendIntent}
                    onChange={(e) => setSendIntent(e.target.value)}
                    className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-200"
                  >
                    {MODES.map((m) => (
                      <option key={m.key} value={m.key}>
                        {m.emoji} {m.key}
                      </option>
                    ))}
                  </select>
                  <ActionButton
                    onClick={sendRequest}
                    disabled={busy || !sendUserId.trim()}
                    variant="primary"
                  >
                    Send Request
                  </ActionButton>
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  For testing only — this will be replaced by discovery UI integration.
                </p>
              </div>
              )}
            </>
          )}
        </Section>

        {/* Who's Here - Connection Awareness */}
        <div className="mt-6">
          <Section title="👀 Who's Here" right={
            <div className="flex items-center gap-2">
              {hiddenUsers.length > 0 && (
                <button
                  onClick={() => setShowHiddenDrawer(!showHiddenDrawer)}
                  className="text-xs text-slate-400 hover:text-slate-600 underline"
                >
                  {hiddenUsers.length} hidden
                </button>
              )}
              <button
                onClick={toggleDiscoverable}
                disabled={busy}
                className={`rounded-xl border px-4 py-2 text-sm font-medium transition-all ${
                  prefs?.discoverable
                    ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                    : "border-slate-200 text-slate-500 hover:bg-slate-50"
                }`}
              >
                {prefs?.discoverable ? "✓ Discoverable" : "👤 Hidden"}
              </button>
            </div>
          }>
            {/* Discoverable helper text */}
            <div className="text-sm text-slate-500 mb-4">
              {prefs?.discoverable ? (
                <span className="text-emerald-600">
                  ✓ You&apos;re visible to others in their suggestions. Turn off to go private.
                </span>
              ) : (
                <span>
                  👆 Turn on <strong>Discoverable</strong> to appear in others&apos; suggestions. Off = private mode.
                </span>
              )}
            </div>

            {/* Hidden Users Drawer */}
            {showHiddenDrawer && hiddenUsers.length > 0 && (
              <div className="mb-4 rounded-xl border border-slate-200 p-3 bg-slate-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Hidden Users</span>
                  <button
                    onClick={() => setShowHiddenDrawer(false)}
                    className="text-slate-400 hover:text-slate-600 text-xs"
                  >
                    ✕ Close
                  </button>
                </div>
                <div className="space-y-2">
                  {hiddenUsers.map((u) => (
                    <div key={u.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-slate-100">
                      <span className="text-sm text-slate-700">{u.display_name}</span>
                      <button
                        onClick={() => unhideSuggestion(u.id)}
                        disabled={busy}
                        className="text-xs text-rose-500 hover:text-rose-700 disabled:opacity-50"
                      >
                        Unhide
                      </button>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  Hidden users won&apos;t appear in your suggestions. Unhide to see them again.
                </p>
              </div>
            )}

            {/* Counts by Mode */}
            {!counts ? (
              <div className="text-sm text-slate-400 animate-pulse">Loading presence…</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { key: "friendship", label: "Friendship", emoji: "🤝", color: "text-amber-600 bg-amber-50 border-amber-200 hover:bg-amber-100" },
                  { key: "business", label: "Business", emoji: "💼", color: "text-blue-600 bg-blue-50 border-blue-200 hover:bg-blue-100" },
                  { key: "community", label: "Community", emoji: "🌍", color: "text-emerald-600 bg-emerald-50 border-emerald-200 hover:bg-emerald-100" },
                  { key: "dating", label: "Dating", emoji: "💕", color: "text-rose-600 bg-rose-50 border-rose-200 hover:bg-rose-100" },
                ].map((m) => (
                  <button
                    key={m.key}
                    className={`rounded-xl border px-4 py-4 text-left transition-all disabled:opacity-50 ${
                      suggestMode === m.key ? m.color + " ring-2 ring-offset-1" : "bg-white border-slate-200 hover:bg-slate-50"
                    }`}
                    onClick={() => loadSuggestions(m.key)}
                    disabled={busy}
                  >
                    <div className="text-xs text-slate-500 flex items-center gap-1">
                      <span>{m.emoji}</span>
                      <span>{m.label}</span>
                    </div>
                    <div className="text-2xl font-bold text-slate-800 mt-1">{counts[m.key] ?? 0}</div>
                    <div className="text-xs text-slate-400 mt-0.5">available</div>
                  </button>
                ))}
              </div>
            )}

            {/* Suggestions Panel */}
            {suggestMode && (
              <div className="mt-4 rounded-xl border border-slate-200 p-4 bg-white">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="text-sm font-semibold text-slate-700 capitalize flex items-center gap-2">
                    {MODES.find(m => m.key === suggestMode)?.emoji} {suggestMode} suggestions
                  </div>
                  <div className="flex items-center gap-2">
                    <ActionButton 
                      onClick={showMore} 
                      disabled={busy || showMoreCooldown}
                    >
                      {showMoreCooldown ? "⏳ Loading…" : "↻ Show more"}
                    </ActionButton>
                    <button
                      onClick={() => { setSuggestMode(null); setSuggestions([]); }}
                      className="text-slate-400 hover:text-slate-600 text-sm"
                    >
                      ✕ Close
                    </button>
                  </div>
                </div>

                {suggestions.length === 0 ? (
                  <div className="text-sm text-slate-400 py-6 text-center">
                    <div className="text-2xl mb-2">🌱</div>
                    No suggestions right now. Try &quot;Show more&quot;, switch mode, or invite someone!
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {suggestions.map((u) => {
                      const status = getConnectionStatus(u.id, suggestMode);
                      const isSending = status === "sending";
                      const isPending = status === "pending";
                      
                      return (
                        <div key={u.id} className="flex items-center justify-between gap-3 py-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="h-10 w-10 rounded-full border-2 border-slate-200 overflow-hidden bg-gradient-to-br from-rose-100 to-purple-100 flex items-center justify-center text-sm shrink-0">
                              {u.avatar_url ? (
                                <img alt={u.display_name} src={u.avatar_url} className="h-full w-full object-cover" />
                              ) : (
                                <span className="opacity-60">💜</span>
                              )}
                            </div>
                            <div className="min-w-0">
                              <div className="font-medium text-slate-800 truncate">{u.display_name}</div>
                              <div className="text-xs text-slate-400">
                                {u.verified_status && <span className="text-emerald-600">✓ verified · </span>}
                                <span className="text-slate-500">Open to {suggestMode}</span>
                                {u.location && <span> · {u.location}</span>}
                              </div>
                              {/* Pending microcopy */}
                              {isPending ? (
                                <div className="text-xs text-amber-600 mt-0.5 font-medium">
                                  ⏳ Waiting for their response
                                </div>
                              ) : (
                                <div className="text-xs text-slate-300 mt-0.5">
                                  Discoverable · {u.connection_mode === suggestMode ? `Primary: ${u.connection_mode}` : `Also open to ${suggestMode}`}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            <ActionButton
                              onClick={() => sendFromSuggestion(u.id, suggestMode)}
                              disabled={isSending || isPending || busy}
                              variant={isPending ? "success" : "primary"}
                              aria-label={`Send connection request to ${u.display_name}`}
                            >
                              {isSending ? "Sending…" : isPending ? "✓ Sent" : "Connect"}
                            </ActionButton>
                            <ActionButton
                              onClick={() => hideSuggestion(u.id)}
                              disabled={busy}
                            >
                              Hide
                            </ActionButton>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Discoverability tip */}
            {!suggestMode && !prefs?.discoverable && (
              <div className="mt-4 text-xs text-slate-400 text-center">
                💡 Turn on &quot;Discoverable&quot; above to appear in others&apos; suggestions.
              </div>
            )}
          </Section>
        </div>

        {/* Trust Invites Section */}
        <div className="mt-6">
          <Section title="🔗 Invite Someone You Trust">
            <div className="text-sm text-slate-500 mb-4">
              Invite links are social-only (no rewards). Best for friends, business partners, and community builders.
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Create Invite Card */}
              <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/50">
                <div className="text-sm font-semibold text-slate-700 mb-3">Create Invite</div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Mode</label>
                    <select
                      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-200"
                      value={inviteMode}
                      onChange={(e) => setInviteMode(e.target.value)}
                      disabled={invitesBusy || busy}
                    >
                      <option value="friendship">🤝 Friendship</option>
                      <option value="business">💼 Business</option>
                      <option value="community">🌍 Community</option>
                      <option value="dating">💕 Dating</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Max Uses</label>
                    <input
                      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-200"
                      type="number"
                      min="1"
                      max="10000"
                      value={inviteMaxUses}
                      onChange={(e) => setInviteMaxUses(e.target.value)}
                      disabled={invitesBusy || busy}
                    />
                  </div>

                  <div>
                    <label className="text-xs text-slate-500 mb-1 block" id="ttl-label">
                      TTL (hours)
                      <span 
                        className="ml-1 text-slate-400 font-normal cursor-help" 
                        title="Time-to-live: How long this invite link stays active before expiring"
                        role="tooltip"
                        id="ttl-tooltip"
                        aria-label="TTL means Time-to-live: How long this invite link stays active before expiring"
                        tabIndex={0}
                      >ⓘ</span>
                    </label>
                    <input
                      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-200"
                      type="number"
                      min="1"
                      max={24 * 365}
                      value={inviteTtlHours}
                      onChange={(e) => setInviteTtlHours(e.target.value)}
                      disabled={invitesBusy || busy}
                      aria-describedby="ttl-tooltip"
                    />
                    <div className="text-[10px] text-slate-400 mt-1">How long the link stays active (168 = 7 days)</div>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="text-xs text-slate-500 mb-1 block">Note (optional)</label>
                    <input
                      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-200"
                      value={inviteNote}
                      onChange={(e) => setInviteNote(e.target.value)}
                      placeholder="Example: Let's connect for business collabs."
                      disabled={invitesBusy || busy}
                      maxLength={200}
                    />
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <ActionButton
                    onClick={createInvite}
                    disabled={invitesBusy || busy}
                    variant="primary"
                  >
                    {invitesBusy ? "Creating…" : "Create Invite"}
                  </ActionButton>

                  {lastInviteLink && (
                    <ActionButton
                      onClick={() => copyText(lastInviteLink)}
                      disabled={invitesBusy || busy}
                    >
                      📋 Copy Link
                    </ActionButton>
                  )}
                </div>

                {lastInviteLink && (
                  <div className="mt-3 rounded-xl border border-slate-200 p-3 bg-white">
                    <div className="text-xs text-slate-400 mb-1">Invite Link</div>
                    <div className="text-sm font-mono break-all text-slate-700">{lastInviteLink}</div>
                  </div>
                )}
              </div>

              {/* Recent Invites Card */}
              <div className="rounded-xl border border-slate-200 p-4 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-semibold text-slate-700">Your Recent Invites</div>
                  <ActionButton
                    onClick={refreshInvites}
                    disabled={invitesBusy || busy}
                  >
                    ↻ Refresh
                  </ActionButton>
                </div>

                {(!invites || invites.length === 0) ? (
                  <div className="text-sm text-slate-400 py-6 text-center">
                    <div className="text-2xl mb-2">📬</div>
                    No invites yet. Create one to bring someone you trust into the system.
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
                    {invites.slice(0, 10).map((inv) => {
                      const link = buildInviteLink(inv.code);
                      const uses = inv.uses ?? 0;
                      const max = inv.max_uses;
                      const mode = inv.mode || "friendship";
                      const modeEmoji = MODES.find(m => m.key === mode)?.emoji || "🤝";
                      return (
                        <div key={inv.code} className="py-2.5 flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                              <span>{modeEmoji}</span>
                              <span className="capitalize">{mode}</span>
                            </div>
                            <div className="text-xs text-slate-400 mt-0.5">
                              Uses: {uses}{max ? `/${max}` : ""}{inv.expires_at ? " · Expires set" : ""}
                            </div>
                            {inv.note && (
                              <div className="text-xs text-slate-400 mt-0.5 truncate max-w-[200px]">
                                {inv.note}
                              </div>
                            )}
                            <div className="text-[10px] text-slate-300 font-mono mt-1">
                              {inv.code}
                            </div>
                          </div>

                          <div className="shrink-0 flex gap-1.5">
                            <button
                              className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                              onClick={() => copyText(link)}
                              disabled={invitesBusy || busy}
                            >
                              Copy
                            </button>
                            <button
                              className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                              onClick={() => {
                                setLastInviteLink(link);
                                setInviteMode(mode);
                                if (inv.note) setInviteNote(inv.note);
                              }}
                              disabled={invitesBusy || busy}
                            >
                              Use
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </Section>
        </div>

        {/* Connection Inbox - Tabbed Interface */}
        <div className="mt-6">
          <Section title="📬 Your Connections Inbox">
            {/* Tabs */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <TabButton 
                active={inboxTab === "incoming"} 
                onClick={() => setInboxTab("incoming")} 
                count={inboxCounts.incoming}
              >
                📥 Incoming
              </TabButton>
              <TabButton 
                active={inboxTab === "sent"} 
                onClick={() => setInboxTab("sent")} 
                count={inboxCounts.sent}
              >
                📤 Sent
              </TabButton>
              <TabButton 
                active={inboxTab === "connected"} 
                onClick={() => setInboxTab("connected")} 
                count={inboxCounts.connected}
              >
                💜 Connected
              </TabButton>
            </div>

            {/* Tab Content */}
            <div className="space-y-3">
              {/* Incoming Tab */}
              {inboxTab === "incoming" && (
                <>
                  {incoming.length === 0 ? (
                    <InboxEmptyState 
                      title="No new requests right now" 
                      body="When someone reaches out, you'll see it here."
                      emoji="🌙"
                    />
                  ) : (
                    incoming.map((edge) => {
                      const isBusy = !!actionBusy[edge.id];
                      const isNew = isNewRequest(edge.id);
                      return (
                        <InboxRowCard
                          key={edge.id}
                          user={edge.other_user}
                          intent={edge.intent}
                          meta={
                            <span className="flex items-center gap-2">
                              {isNew && (
                                <span className="inline-flex items-center rounded-full bg-rose-100 border border-rose-200 px-2 py-0.5 text-[10px] font-bold text-rose-600 uppercase tracking-wide animate-pulse">
                                  NEW
                                </span>
                              )}
                              <span>New request</span>
                            </span>
                          }
                          right={
                            <>
                              <ActionButton 
                                onClick={() => decline(edge.id)} 
                                disabled={isBusy}
                                aria-label={`Decline request from ${edge.other_user?.display_name || 'user'}`}
                              >
                                Decline
                              </ActionButton>
                              <ActionButton 
                                onClick={() => accept(edge.id)} 
                                disabled={isBusy}
                                variant="primary"
                                aria-label={`Accept request from ${edge.other_user?.display_name || 'user'}`}
                              >
                                {isBusy ? "..." : "Accept"}
                              </ActionButton>
                            </>
                          }
                        />
                      );
                    })
                  )}
                </>
              )}

              {/* Sent Tab */}
              {inboxTab === "sent" && (
                <>
                  {outgoing.length === 0 ? (
                    <InboxEmptyState 
                      title="No requests sent yet" 
                      body="Send a connection request from Discover or a profile."
                      emoji="📭"
                    />
                  ) : (
                    outgoing.map((edge) => (
                      <InboxRowCard
                        key={edge.id}
                        user={edge.other_user}
                        intent={edge.intent}
                        meta="⏳ Waiting for their response"
                        right={
                          <span className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700">
                            ✓ Sent
                          </span>
                        }
                      />
                    ))
                  )}
                </>
              )}

              {/* Connected Tab */}
              {inboxTab === "connected" && (
                <>
                  {/* Inline error display for profile loading */}
                  {profileModalError && (
                    <div className="mb-4 p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
                      <div className="flex items-center justify-between gap-3">
                        <span>{profileModalError}</span>
                        <button 
                          onClick={() => setProfileModalError(null)}
                          className="text-red-500 hover:text-red-700 font-medium"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {accepted.length === 0 ? (
                    <InboxEmptyState 
                      title="No connections yet (for now)" 
                      body="Start in Discover — your first connection is one good click away."
                      emoji="🌱"
                    />
                  ) : (
                    accepted.map((edge) => {
                      const otherId = edge.other_user?.id;
                      const displayName = edge.other_user?.display_name || 'user';
                      const isMsgBusy = !!msgBusyByUser[String(otherId)];
                      return (
                        <InboxRowCard
                          key={edge.id}
                          user={edge.other_user}
                          intent={edge.intent}
                          meta="Connected"
                          right={
                            <>
                              <ActionButton 
                                onClick={() => openProfileModal(otherId, displayName)}
                                disabled={profileModalLoading}
                                aria-label={`View profile of ${displayName}`}
                              >
                                {profileModalLoading ? "Loading…" : "View"}
                              </ActionButton>
                              <ActionButton 
                                onClick={async () => {
                                  if (!otherId || isMsgBusy) return;
                                  try {
                                    await openChatAndNavigate({
                                      other_user_id: otherId,
                                      token,
                                      navigate,
                                      onBusy: (v) => setMsgBusy(otherId, v),
                                    });
                                  } catch (e) {
                                    showToast("Couldn't open chat", "Please try again.");
                                    console.error("[Connections] Message error:", e);
                                  }
                                }}
                                disabled={isMsgBusy}
                                variant="primary"
                                aria-label={`Message ${displayName}`}
                              >
                                {isMsgBusy ? "Opening…" : "Message"}
                              </ActionButton>
                            </>
                          }
                        />
                      );
                    })
                  )}
                </>
              )}
            </div>
          </Section>
        </div>

        {/* Blocked Users - Collapsed by default (less prominent) */}
        {blocked.length > 0 && (
          <div className="mt-6">
            <div className="rounded-2xl border border-slate-200 p-5 shadow-sm bg-white">
              <button
                onClick={() => setShowBlocked(!showBlocked)}
                className="w-full flex items-center justify-between text-left"
              >
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-slate-800">🚫 Blocked Users</h2>
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 text-xs font-medium text-slate-600">
                    {blocked.length}
                  </span>
                </div>
                <span className="text-slate-400 text-sm">
                  {showBlocked ? "Hide ▲" : "Show ▼"}
                </span>
              </button>
              
              {showBlocked && (
                <div className="space-y-3 mt-4">
                  {blocked.map((edge) => (
                    <InboxRowCard
                      key={edge.id}
                      user={edge.other_user}
                      intent={edge.intent}
                      meta="Blocked"
                      right={
                        <ActionButton
                          onClick={() => unblock(edge.other_user?.id)}
                          disabled={busy || !edge.other_user?.id}
                        >
                          Unblock
                        </ActionButton>
                      }
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="mt-6 text-center text-sm text-slate-400 animate-pulse">
            Loading connections…
          </div>
        )}

        {/* Empty State */}
        {emptyState && !loading && (
          <div className="mt-8 rounded-2xl border border-dashed border-slate-300 p-8 bg-slate-50/50 text-center">
            <div className="text-4xl mb-3">🌱</div>
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Start Building Connections</h3>
            <p className="text-sm text-slate-500 max-w-md mx-auto">
              This is your calm space for intentional connections. Use the test form above to send your first request, 
              or wait for others to reach out based on your connection mode.
            </p>
          </div>
        )}
      </div>

      {/* Connection Confidence Toast */}
      <ConnectionToast toast={toast} onDismiss={dismissToast} />
      
      {/* Canonical Profile View Modal - uses shared profileViewer hook */}
      <ProfileViewModal
        open={profileViewer.open}
        onClose={profileViewer.close}
        profile={profileViewer.profile}
        isSelf={profileViewer.isSelf}
        source={profileViewer.source || "connections"}
      />
    </PageShell>
  );
}
